import type { Session } from '@supabase/supabase-js'
import { AlertCircle, CheckCircle2, LoaderCircle } from 'lucide-react'
import { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { AppShell } from './components/AppShell'
import { AttachmentDrawer } from './components/AttachmentDrawer'
import { CreateProjectDialog, type ProjectCreationInput } from './components/CreateProjectDialog'
import { LoginScreen } from './components/LoginScreen'
import { ProjectHistoryDrawer } from './components/ProjectHistoryDrawer'
import { ProjectViewNav } from './components/ProjectViewNav'
import { ProfileDrawer } from './components/ProfileDrawer'
import { ReferenceViewNav } from './components/ReferenceViewNav'
import { ReminderDrawer } from './components/ReminderDrawer'
import { TrashDrawer } from './components/TrashDrawer'
import { createProjectArchive, downloadArchiveBlob, restoreProjectArchive } from './lib/projectArchive'
import { applyCandidateChanges, applyReferenceDecision, materializeProjectProcess, processPhases, projectForReference, referenceUpdateAvailable } from './lib/project'
import { remindersForProjects } from './lib/reminders'
import {
  createProject,
  downloadProjectAttachment,
  getExport,
  getGeneralReference,
  listProjectAttachments,
  listPresentationEditions,
  listGeneralPresentationEditions,
  listProjects,
  listTrashedProjects,
  listProjectVersions,
  getMyOperatorProfile,
  markImportApplied,
  registerExport,
  saveProjectSnapshot,
  saveGeneralReference,
  storeImport,
  publishPresentationEdition,
  downloadPresentationEdition,
  updateAttachmentMetadata,
  updateMyOperatorProfile,
  uploadProjectAttachment,
  restoreTrashedProject,
  trashProject,
} from './lib/repository'
import { parseRoute, routePath, type AppRoute, type ProjectRouteView, type ReferenceRouteView } from './lib/routes'
import { hasSupabaseConfig, requireSupabase, supabase } from './lib/supabase'
import type { AttachmentMetadata, GeneralReference, GeneralReferenceRow, ImportRow, OperatorProfile, PresentationEditionRow, ProjectAttachment, ProjectReferenceChoice, ProjectRow, ProjectSnapshot, ProjectVersionRow } from './types'
import { DifferencesView } from './views/DifferencesView'
import { PortfolioView } from './views/PortfolioView'
import { ProcessCanvas2D } from './views/ProcessCanvas2D'
import { ProjectCockpit } from './views/ProjectCockpit'
import { ProjectTableView } from './views/ProjectTableView'
import { ProjectRouteEditor } from './views/ProjectRouteEditor'
import { PresentationStudio } from './views/PresentationStudio'
import { ReferenceOverview } from './views/ReferenceOverview'
import { ReferenceRouteEditor } from './views/ReferenceRouteEditor'
import { ReferenceUpdateView } from './views/ReferenceUpdateView'

const ProcessCanvas3D = lazy(() => import('./views/ProcessCanvas3D').then((module) => ({ default: module.ProcessCanvas3D })))

function operatorAvatarUrl(email?: string | null) {
  return email?.trim().toLowerCase() === 'akane@sabluxgroup.com' ? '/assets/people/aziz-kane.png' : undefined
}

export default function App() {
  const [session, setSession] = useState<Session | null>(null)
  const [authReady, setAuthReady] = useState(false)
  const [projects, setProjects] = useState<ProjectRow[]>([])
  const [reference, setReference] = useState<GeneralReferenceRow | null>(null)
  const [route, setRoute] = useState<AppRoute>(() => parseRoute())
  const [pendingImport, setPendingImport] = useState<ImportRow | null>(null)
  const [busy, setBusy] = useState(false)
  const [creating, setCreating] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [versions, setVersions] = useState<ProjectVersionRow[]>([])
  const [attachments, setAttachments] = useState<ProjectAttachment[]>([])
  const [presentationEditions, setPresentationEditions] = useState<PresentationEditionRow[]>([])
  const [attachmentTarget, setAttachmentTarget] = useState<{ stepId?: string; stepName?: string } | null>(null)
  const [loading, setLoading] = useState(false)
  const [notice, setNotice] = useState<{ tone: 'success' | 'error'; text: string } | null>(null)
  const [profile, setProfile] = useState<OperatorProfile | null>(null)
  const [profileOpen, setProfileOpen] = useState(false)
  const [remindersOpen, setRemindersOpen] = useState(false)
  const [trashOpen, setTrashOpen] = useState(false)
  const [trashedProjects, setTrashedProjects] = useState<ProjectRow[]>([])
  const [operatorAccess, setOperatorAccess] = useState<'checking' | 'authorized' | 'unauthorized' | 'error'>('checking')

  const selected = route.kind === 'project' ? projects.find((project) => project.code === route.projectCode) ?? null : null
  const referenceProject = reference && projects[0] ? projectForReference(reference, projects[0]) : null
  const hasReferenceUpdate = Boolean(selected && reference && referenceUpdateAvailable(selected.snapshot, reference.content))
  const reminders = useMemo(
    () => profile?.notification_preferences?.in_app ? remindersForProjects(projects) : [],
    [profile?.notification_preferences?.in_app, projects],
  )

  const navigate = useCallback((next: AppRoute, replace = false) => {
    const path = routePath(next)
    if (replace) window.history.replaceState({}, '', path)
    else window.history.pushState({}, '', path)
    setRoute(next)
  }, [])

  useEffect(() => {
    const onPopState = () => setRoute(parseRoute())
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  useEffect(() => {
    if (!supabase) {
      setAuthReady(true)
      return
    }
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      setAuthReady(true)
    })
    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession)
      setAuthReady(true)
    })
    return () => data.subscription.unsubscribe()
  }, [])

  const refreshProjects = useCallback(async () => {
    setLoading(true)
    setOperatorAccess('checking')
    try {
      const nextProfile = await getMyOperatorProfile()
      const [nextProjects, nextReference] = await Promise.all([listProjects(), getGeneralReference()])
      setProjects(nextProjects)
      setReference(nextReference)
      setProfile(nextProfile)
      setOperatorAccess('authorized')
    } catch (error) {
      const code = typeof error === 'object' && error && 'code' in error ? String(error.code) : ''
      if (code === 'PGRST116') setOperatorAccess('unauthorized')
      else setOperatorAccess('error')
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Impossible de charger les projets.' })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (session) void refreshProjects()
    else { setProjects([]); setReference(null); setProfile(null); setOperatorAccess('checking') }
  }, [refreshProjects, session])

  useEffect(() => {
    if (!session) {
      setAttachments([])
      setPresentationEditions([])
      return
    }
    if (route.kind === 'reference' && reference) {
      setAttachments([])
      listGeneralPresentationEditions(reference.reference_key)
        .then(setPresentationEditions)
        .catch((error) => setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Les éditions générales n’ont pas pu être chargées.' }))
      return
    }
    if (!selected) {
      setAttachments([])
      setPresentationEditions([])
      return
    }
    Promise.all([listProjectAttachments(selected.id), listPresentationEditions(selected.id)])
      .then(([nextAttachments, nextEditions]) => {
        setAttachments(nextAttachments)
        setPresentationEditions(nextEditions)
      })
      .catch((error) => setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Les pièces jointes ou les éditions de présentation n’ont pas pu être chargées.' }))
  }, [reference, route.kind, selected, session])

  useEffect(() => {
    if (loading || route.kind !== 'project' || selected || !projects.length) return
    setNotice({ tone: 'error', text: 'Ce projet est introuvable. Retour au portefeuille.' })
    navigate({ kind: 'portfolio' }, true)
  }, [loading, navigate, projects.length, route, selected])

  useEffect(() => {
    if (!notice) return
    const timeout = window.setTimeout(() => setNotice(null), 6000)
    return () => window.clearTimeout(timeout)
  }, [notice])

  function openProject(project: ProjectRow) {
    setPendingImport(null)
    setAttachmentTarget(null)
    navigate({ kind: 'project', projectCode: project.code, view: 'cockpit' })
  }

  function openProjectById(projectId: string) {
    const project = projects.find((item) => item.id === projectId)
    if (!project) return
    setRemindersOpen(false)
    openProject(project)
  }

  function openProjectView(view: ProjectRouteView) {
    if (selected) navigate({ kind: 'project', projectCode: selected.code, view })
  }

  function openReferenceUpdate() {
    setPendingImport(null)
    openProjectView('differences')
  }

  function openReferenceView(view: ReferenceRouteView) {
    navigate({ kind: 'reference', view })
  }

  function slugify(value: string) {
    return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
  }

  async function createNewProject(input: ProjectCreationInput) {
    const sourceProject = projects[0]
    if (!sourceProject || !reference) {
      setNotice({ tone: 'error', text: 'La référence générale n’est pas encore disponible.' })
      return
    }
    setBusy(true)
    try {
      let snapshot = structuredClone(sourceProject.snapshot)
      snapshot.reference = structuredClone(reference.content)
      snapshot.as_of_date = new Date().toISOString().slice(0, 10)
      snapshot.project.id = input.code
      snapshot.project.name = input.name
      snapshot.project.city = input.city
      snapshot.project.country = 'Côte d’Ivoire'
      snapshot.project.owner = 'Sablux Côte d’Ivoire'
      snapshot.project.truth_state = input.category === 'fictif' ? 'prototype_fictif' : 'particularite_projet'
      snapshot.dataset_kind = input.category === 'fictif' ? 'prototype_fictif' : 'projet_reel'
      snapshot.project.derogation_applicability = 'a_confirmer'
      snapshot.project.events = [{ id: crypto.randomUUID(), date: snapshot.as_of_date, type: 'creation_projet', summary: `Projet créé depuis ${input.mode === 'reference' ? 'la référence générale' : input.mode === 'blank' ? 'un parcours vierge' : 'un Excel compatible'}` }]

      if (input.mode === 'reference') {
        delete snapshot.project.process_phases
        delete snapshot.project.process_branches
        snapshot.project.process_source = 'reference_default'
      } else if (input.mode === 'blank') {
        snapshot = materializeProjectProcess(snapshot)
        snapshot.project.process_phases = [{ id: 'PH-01', name: 'Première phase', order: 1, enabled: true, steps: [{ id: 'SP-01-01', name: 'Première étape', order: 1, enabled: true, truth_state: 'particularite_projet' }] }]
      } else {
        if (!input.file) throw new Error('Choisissez un fichier Excel compatible.')
        const { readWorkbookIdentity, readWorkbookStructure } = await import('./services/excel')
        const identity = await readWorkbookIdentity(input.file)
        const exported = await getExport(identity.exportId)
        snapshot = materializeProjectProcess(snapshot)
        snapshot.project.process_phases = await readWorkbookStructure(input.file, exported.source_snapshot)
      }

      const phases = processPhases(snapshot, true)
      const firstPhase = phases.find((phase) => phase.enabled !== false)
      const firstStep = firstPhase?.steps.find((step) => step.enabled !== false)
      if (!firstPhase || !firstStep) throw new Error('Le projet doit contenir au moins une phase et une étape actives.')
      snapshot.project.current_phase_id = firstPhase.id
      snapshot.project.current_step_id = firstStep.id
      snapshot.project.status_summary = `${firstStep.name} — Non commencé`
      snapshot.project.step_states = phases.flatMap((phase) => phase.steps.map((step) => ({ step_id: step.id, status: 'Non commencé' as const, truth_state: input.category === 'fictif' ? 'prototype_fictif' as const : 'particularite_projet' as const })))

      const created = await createProject({
        slug: slugify(input.name),
        code: input.code,
        name: input.name,
        scenario: input.category === 'fictif' ? 'Projet fictif / démonstration' : 'Projet réel',
        city: input.city,
      }, snapshot)
      setProjects((current) => [...current, created].sort((left, right) => left.name.localeCompare(right.name, 'fr')))
      setCreating(false)
      openProject(created)
      setNotice({ tone: 'success', text: `${created.name} a été créé. Son parcours peut être adapté immédiatement.` })
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Le projet n’a pas pu être créé.' })
    } finally {
      setBusy(false)
    }
  }

  async function saveProfile(input: { displayName: string; phone?: string; notificationPreferences: OperatorProfile['notification_preferences'] }) {
    setBusy(true)
    try {
      const updated = await updateMyOperatorProfile(input)
      setProfile(updated)
      setNotice({ tone: 'success', text: 'Votre profil a été enregistré.' })
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Le profil n’a pas pu être enregistré.' })
    } finally {
      setBusy(false)
    }
  }

  async function setPassword(password: string) {
    setBusy(true)
    try {
      const { error } = await requireSupabase().auth.updateUser({ password })
      if (error) throw error
      setNotice({ tone: 'success', text: 'Le mot de passe a été enregistré. Le lien par email reste disponible.' })
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Le mot de passe n’a pas pu être enregistré.' })
      throw error
    } finally {
      setBusy(false)
    }
  }

  async function openTrash() {
    setBusy(true)
    try {
      setTrashedProjects(await listTrashedProjects())
      setTrashOpen(true)
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'La corbeille n’a pas pu être chargée.' })
    } finally {
      setBusy(false)
    }
  }

  async function restoreFromTrash(project: ProjectRow) {
    setBusy(true)
    try {
      const restored = await restoreTrashedProject(project.id)
      setProjects((current) => [...current, restored].sort((left, right) => left.name.localeCompare(right.name, 'fr')))
      setTrashedProjects((current) => current.filter((item) => item.id !== restored.id))
      setNotice({ tone: 'success', text: `${restored.name} a été restauré.` })
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Le projet n’a pas pu être restauré.' })
    } finally {
      setBusy(false)
    }
  }

  async function moveToTrash() {
    if (!selected) return
    if (!window.confirm(`Mettre « ${selected.name} » dans la corbeille ? Vous pourrez le restaurer.`)) return
    setBusy(true)
    try {
      const trashed = await trashProject(selected)
      setProjects((current) => current.filter((project) => project.id !== trashed.id))
      setTrashedProjects((current) => [trashed, ...current.filter((project) => project.id !== trashed.id)])
      navigate({ kind: 'portfolio' }, true)
      setNotice({ tone: 'success', text: `${trashed.name} a été placé dans la corbeille.` })
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Le projet n’a pas pu être placé dans la corbeille.' })
      await refreshProjects()
    } finally {
      setBusy(false)
    }
  }

  async function downloadProjectArchive() {
    if (!selected) return
    setBusy(true)
    try {
      const archive = await createProjectArchive(selected)
      downloadArchiveBlob(archive.blob, selected)
      setNotice({ tone: 'success', text: `L’archive complète de ${selected.name} a été téléchargée.` })
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'L’archive n’a pas pu être créée.' })
    } finally {
      setBusy(false)
    }
  }

  async function restoreArchive(file: File) {
    setBusy(true)
    try {
      const result = await restoreProjectArchive(file)
      setProjects((current) => [...current, result.project].sort((left, right) => left.name.localeCompare(right.name, 'fr')))
      openProject(result.project)
      setNotice({ tone: 'success', text: `${result.project.name} a été restauré avec ${result.restoredAttachments} pièce${result.restoredAttachments > 1 ? 's' : ''} jointe${result.restoredAttachments > 1 ? 's' : ''}.` })
    } catch (error) {
      await refreshProjects()
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Cette archive n’a pas pu être restaurée.' })
    } finally {
      setBusy(false)
    }
  }

  async function openHistory() {
    if (!selected) return
    setBusy(true)
    try {
      setVersions(await listProjectVersions(selected.id))
      setHistoryOpen(true)
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'L’historique n’a pas pu être chargé.' })
    } finally {
      setBusy(false)
    }
  }

  async function restoreVersion(version: ProjectVersionRow) {
    if (!selected) return
    const next = structuredClone(version.snapshot)
    next.project.events.unshift({ id: crypto.randomUUID(), date: next.as_of_date, type: 'restauration_version', summary: `Version ${version.version} restaurée comme nouvel état` })
    const saved = await saveSnapshot(next, `Restauration de la version ${version.version}`)
    if (saved) {
      setHistoryOpen(false)
      setNotice({ tone: 'success', text: `La version ${version.version} a été restaurée sans effacer l’historique.` })
    }
  }

  function replaceProject(updated: ProjectRow) {
    setProjects((current) => current.map((project) => project.id === updated.id ? updated : project))
  }

  async function saveSnapshot(snapshot: ProjectSnapshot, summary: string): Promise<boolean> {
    if (!selected) return false
    setBusy(true)
    try {
      const updated = await saveProjectSnapshot(selected, snapshot, summary)
      replaceProject(updated)
      setNotice({ tone: 'success', text: 'La mise à jour a été enregistrée dans le journal du projet.' })
      return true
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'La mise à jour n’a pas pu être enregistrée.' })
      await refreshProjects()
      return false
    } finally {
      setBusy(false)
    }
  }

  async function uploadAttachment(file: File, replaces?: ProjectAttachment) {
    if (!selected || !attachmentTarget) return
    setBusy(true)
    try {
      const result = await uploadProjectAttachment({
        project: selected,
        stepId: attachmentTarget.stepId ?? replaces?.step_id,
        stepLabel: attachmentTarget.stepName,
        file,
        replaces,
      })
      replaceProject(result.project)
      setAttachments((current) => [result.attachment, ...current])
      setNotice({ tone: 'success', text: replaces ? 'La nouvelle version du fichier a été ajoutée.' : 'Le fichier a été joint au projet.' })
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Le fichier n’a pas pu être ajouté.' })
    } finally {
      setBusy(false)
    }
  }

  async function qualifyAttachment(attachment: ProjectAttachment, metadata: AttachmentMetadata) {
    setBusy(true)
    try {
      const updated = await updateAttachmentMetadata(attachment.id, metadata)
      setAttachments((current) => current.map((item) => item.id === updated.id ? updated : item))
      setNotice({ tone: 'success', text: 'Les précisions facultatives du fichier ont été enregistrées.' })
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Les précisions n’ont pas pu être enregistrées.' })
    } finally {
      setBusy(false)
    }
  }

  async function downloadAttachment(attachment: ProjectAttachment) {
    try {
      await downloadProjectAttachment(attachment)
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Le fichier n’a pas pu être téléchargé.' })
    }
  }

  async function createPresentationEdition(input: Omit<Parameters<typeof publishPresentationEdition>[0], 'project'>) {
    const contextProject = route.kind === 'reference' ? referenceProject : selected
    if (!contextProject) throw new Error('Le contexte de présentation n’est pas disponible.')
    const edition = await publishPresentationEdition({ ...input, project: contextProject, reference: route.kind === 'reference' ? reference ?? undefined : undefined })
    setPresentationEditions((current) => [edition, ...current])
    return edition
  }

  async function downloadPresentation(edition: PresentationEditionRow) {
    try {
      await downloadPresentationEdition(edition)
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Le fichier de présentation n’a pas pu être téléchargé.' })
    }
  }

  async function exportExcel() {
    if (!selected) return
    setBusy(true)
    try {
      const { downloadProjectWorkbook } = await import('./services/excel')
      const exportRow = await registerExport(selected)
      await downloadProjectWorkbook(selected, exportRow)
      setNotice({ tone: 'success', text: `Excel ${exportRow.export_id} téléchargé. Il reste autonome hors de Suivi Aziz.` })
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'L’export Excel a échoué.' })
    } finally {
      setBusy(false)
    }
  }

  async function exportWord() {
    if (!selected) return
    setBusy(true)
    try {
      const { downloadProjectWord } = await import('./services/word')
      const exportRow = await registerExport(selected, 'docx')
      await downloadProjectWord(selected, exportRow, attachments)
      setNotice({ tone: 'success', text: `Note Word ${exportRow.export_id} téléchargée. Elle reste éditable et autonome.` })
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'L’export Word a échoué.' })
    } finally {
      setBusy(false)
    }
  }

  async function importExcel(file: File) {
    if (!selected) return
    setBusy(true)
    try {
      const { inspectReturnedWorkbook, readWorkbookIdentity } = await import('./services/excel')
      const identity = await readWorkbookIdentity(file)
      if (identity.projectDatabaseId && identity.projectDatabaseId !== selected.id) {
        throw new Error('Cet Excel appartient à un autre projet.')
      }
      const exportRow = await getExport(identity.exportId)
      const inspection = await inspectReturnedWorkbook(file, exportRow, selected.snapshot)
      const imported = await storeImport(selected, exportRow, file, inspection.candidates)
      setPendingImport(imported)
      openProjectView('differences')
      setNotice({ tone: 'success', text: 'L’Excel a été lu. Vérifiez maintenant chaque différence proposée.' })
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Ce fichier n’a pas pu être analysé.' })
    } finally {
      setBusy(false)
    }
  }

  async function importWord(file: File) {
    if (!selected) return
    setBusy(true)
    try {
      const { inspectReturnedWord, readWordIdentity } = await import('./services/word')
      const identity = await readWordIdentity(file)
      if (identity.projectDatabaseId && identity.projectDatabaseId !== selected.id) {
        throw new Error('Ce document Word appartient à un autre projet.')
      }
      const exportRow = await getExport(identity.exportId)
      if (exportRow.format !== 'docx') throw new Error('Cet identifiant correspond à un autre format.')
      const inspection = await inspectReturnedWord(file, exportRow, selected.snapshot)
      const imported = await storeImport(selected, exportRow, file, inspection.candidates)
      setPendingImport(imported)
      openProjectView('differences')
      setNotice({ tone: 'success', text: 'Le Word a été lu. Vérifiez chaque différence structurée proposée.' })
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Ce document Word n’a pas pu être analysé.' })
    } finally {
      setBusy(false)
    }
  }

  function toggleCandidate(candidateId: string) {
    setPendingImport((current) => current ? {
      ...current,
      candidates: current.candidates.map((candidate) => candidate.id === candidateId ? { ...candidate, accepted: !candidate.accepted } : candidate),
    } : null)
  }

  function acceptAll() {
    setPendingImport((current) => current ? {
      ...current,
      candidates: current.candidates.map((candidate) => ({ ...candidate, accepted: true })),
    } : null)
  }

  async function applyImport() {
    if (!selected || !pendingImport) return
    const accepted = pendingImport.candidates.filter((candidate) => candidate.accepted)
    if (!accepted.length) return
    setBusy(true)
    try {
      const next = applyCandidateChanges(selected.snapshot, pendingImport.candidates)
      const formatLabel = pendingImport.format === 'docx' ? 'Word' : 'Excel'
      next.project.events.unshift({
        id: crypto.randomUUID(),
        date: next.as_of_date,
        type: pendingImport.format === 'docx' ? 'retour_word' : 'retour_excel',
        summary: `Retour ${formatLabel} validé · ${accepted.length} changement(s) appliqué(s)`,
      })
      const updated = await saveProjectSnapshot(selected, next, `Retour ${formatLabel} · ${accepted.length} changement(s) accepté(s)`)
      await markImportApplied(pendingImport, pendingImport.candidates)
      replaceProject(updated)
      setPendingImport(null)
      openProjectView('cockpit')
      setNotice({ tone: 'success', text: `${accepted.length} changement(s) ${formatLabel} appliqué(s). La décision est inscrite dans l’historique.` })
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'Les changements n’ont pas pu être appliqués.' })
      await refreshProjects()
    } finally {
      setBusy(false)
    }
  }

  async function saveReference(content: GeneralReference, summary: string): Promise<boolean> {
    if (!reference) return false
    setBusy(true)
    try {
      const updated = await saveGeneralReference(reference, content, summary)
      setReference(updated)
      setNotice({ tone: 'success', text: `La référence générale v${updated.version} a été publiée. Les projets existants sont inchangés.` })
      return true
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'La nouvelle version de la référence n’a pas pu être publiée.' })
      await refreshProjects()
      return false
    } finally {
      setBusy(false)
    }
  }

  async function decideProjectReference(choice: ProjectReferenceChoice) {
    if (!selected || !reference) return
    const labels: Record<ProjectReferenceChoice, string> = {
      adopt: 'Nouvelle référence utilisée',
      adapt: 'Nouvelle référence utilisée et prête à être adaptée',
      keep: 'Référence actuelle conservée',
    }
    try {
      const next = applyReferenceDecision(selected.snapshot, reference.content, choice, selected.version + 1)
      const saved = await saveSnapshot(next, `${labels[choice]} · ${selected.snapshot.reference.id} → ${reference.content.id}`)
      if (!saved) return
      setNotice({ tone: 'success', text: `${labels[choice]}. La décision est inscrite dans l’historique du projet.` })
      openProjectView(choice === 'adapt' ? 'route-editor' : 'cockpit')
    } catch (error) {
      setNotice({ tone: 'error', text: error instanceof Error ? error.message : 'La décision n’a pas pu être enregistrée.' })
    }
  }

  if (!authReady) return <div className="global-loading"><LoaderCircle className="spin" /> Préparation de Suivi Aziz…</div>
  if (!session) return <LoginScreen configured={hasSupabaseConfig} />
  if (operatorAccess === 'checking') return <div className="global-loading"><LoaderCircle className="spin" /> Vérification de votre accès…</div>
  if (operatorAccess === 'unauthorized') return <main className="access-denied"><div><span className="eyebrow">ACCÈS RÉSERVÉ</span><h1>Ce compte n’est pas autorisé.</h1><p>Utilisez l’adresse email d’Alassane Deme ou d’Aziz Kane.</p><button className="secondary-button" onClick={() => void requireSupabase().auth.signOut()} type="button">Se déconnecter</button></div></main>
  if (operatorAccess === 'error') return <main className="access-denied"><div><span className="eyebrow">ACCÈS INDISPONIBLE</span><h1>La vérification n’a pas abouti.</h1><p>Réessayez dans un instant. Si le problème persiste, reconnectez-vous.</p><div className="access-denied-actions"><button className="secondary-button" onClick={() => void refreshProjects()} type="button">Réessayer</button><button className="text-button" onClick={() => void requireSupabase().auth.signOut()} type="button">Se déconnecter</button></div></div></main>

  const projectView = route.kind === 'project' ? route.view : null
  const referenceView = route.kind === 'reference' ? route.view : null
  const projectVisualView = projectView === 'plan-2d' || projectView === 'layers-3d' || projectView === 'pov-3d'
  const referenceVisualView = referenceView === 'plan-2d' || referenceView === 'layers-3d' || referenceView === 'pov-3d'
  const shellTitle = route.kind === 'reference'
    ? referenceView === 'plan-2d' ? 'Plan 2D général' : referenceView === 'layers-3d' ? 'Couches 3D générales' : referenceView === 'pov-3d' ? 'POV général par îlots' : referenceView === 'presentation' ? 'Présentation générale' : referenceView === 'editor' ? 'Nouvelle version de la référence' : 'Référence générale'
    : route.kind === 'project'
      ? projectView === 'plan-2d' ? 'Plan 2D du projet' : projectView === 'layers-3d' ? 'Couches 3D du projet' : projectView === 'pov-3d' ? 'POV du projet par îlots' : projectView === 'table' ? 'Tableau de suivi' : projectView === 'presentation' ? 'Présentation du projet' : projectView === 'route-editor' ? 'Adapter ce parcours' : projectView === 'differences' ? hasReferenceUpdate && !pendingImport ? 'Nouvelle référence' : 'Vérifier les différences' : selected?.name ?? 'Projet'
      : 'Suivi Aziz'
  const shellSubtitle = route.kind === 'reference'
    ? reference ? `${reference.content.id} · version ${reference.version}` : 'Parcours administratif général'
    : selected ? `${selected.code} · ${selected.city}` : undefined

  return (
    <AppShell
      userLabel={profile?.display_name ?? session.user.email ?? 'Opérateur'}
      title={shellTitle}
      subtitle={shellSubtitle}
      onHome={() => { navigate({ kind: 'portfolio' }); setPendingImport(null); setAttachmentTarget(null) }}
      onSignOut={() => requireSupabase().auth.signOut()}
      onOpenProfile={() => setProfileOpen(true)}
      onOpenReminders={() => setRemindersOpen(true)}
      reminderCount={reminders.length}
      userAvatarUrl={operatorAvatarUrl(profile?.email ?? session.user.email)}
    >
      {notice ? <div className={`toast ${notice.tone}`} role="status">{notice.tone === 'success' ? <CheckCircle2 size={19} /> : <AlertCircle size={19} />}{notice.text}</div> : null}
      {loading && !projects.length ? <div className="global-loading"><LoaderCircle className="spin" /> Chargement des projets…</div> : null}
      {!loading || projects.length ? (
        <>
          {route.kind === 'project' && selected && projectView !== 'differences' ? <ProjectViewNav active={(projectVisualView ? 'canvas' : projectView === 'route-editor' ? 'editor' : projectView) as 'cockpit' | 'table' | 'canvas' | 'presentation' | 'editor'} onCockpit={() => openProjectView('cockpit')} onTable={() => openProjectView('table')} onCanvas={() => openProjectView('plan-2d')} onPresentation={() => openProjectView('presentation')} onEditor={() => openProjectView('route-editor')} /> : null}
          {route.kind === 'reference' ? <ReferenceViewNav active={referenceVisualView ? 'plan-2d' : referenceView!} onOverview={() => openReferenceView('overview')} onCanvas={() => openReferenceView('plan-2d')} onPresentation={() => openReferenceView('presentation')} onEditor={() => openReferenceView('editor')} /> : null}
          {route.kind === 'portfolio' ? <PortfolioView projects={projects} reference={reference} userLabel={profile?.display_name ?? session.user.email ?? 'Opérateur'} busy={busy} onOpen={openProject} onCreate={() => setCreating(true)} onOpenReference={openReferenceView} onOpenTrash={() => void openTrash()} onRestoreArchive={(file) => void restoreArchive(file)} /> : null}
          {projectView === 'cockpit' && selected ? <ProjectCockpit project={selected} attachments={attachments} busy={busy} onBack={() => navigate({ kind: 'portfolio' })} onCanvas={() => openProjectView('plan-2d')} onExport={exportExcel} onExportWord={exportWord} onImport={importExcel} onImportWord={importWord} onDownloadArchive={() => void downloadProjectArchive()} onTrash={() => void moveToTrash()} onOpenAttachments={(stepId, stepName) => setAttachmentTarget({ stepId, stepName })} onSave={saveSnapshot} referenceUpdate={hasReferenceUpdate && reference ? { currentId: selected.snapshot.reference.id, targetId: reference.content.id, onOpen: openReferenceUpdate } : undefined} /> : null}
          {projectView === 'table' && selected ? <ProjectTableView project={selected} attachments={attachments} busy={busy} onBack={() => navigate({ kind: 'portfolio' })} onOpenAttachments={(stepId, stepName) => setAttachmentTarget({ stepId, stepName })} onSave={saveSnapshot} /> : null}
          {projectView === 'plan-2d' && selected ? <ProcessCanvas2D scope="project" project={selected} onBack={() => openProjectView('cockpit')} onDifferences={() => openProjectView('differences')} onModeChange={openProjectView} /> : null}
          {(projectView === 'layers-3d' || projectView === 'pov-3d') && selected ? <Suspense fallback={<div className="global-loading"><LoaderCircle className="spin" /> Préparation de la vue 3D…</div>}><ProcessCanvas3D scope="project" mode={projectView} project={selected} onBack={() => openProjectView('cockpit')} onDifferences={() => openProjectView('differences')} onModeChange={openProjectView} /></Suspense> : null}
          {projectView === 'presentation' && selected ? <PresentationStudio scope="project" project={selected} attachments={attachments} editions={presentationEditions.filter((edition) => edition.scope === 'project')} onBack={() => openProjectView('cockpit')} onPublish={createPresentationEdition} onDownload={downloadPresentation} /> : null}
          {projectView === 'route-editor' && selected ? <ProjectRouteEditor project={selected} busy={busy} onBack={() => openProjectView('cockpit')} onHistory={() => void openHistory()} onExport={exportExcel} onSave={saveSnapshot} referenceUpdate={hasReferenceUpdate && reference ? { currentId: selected.snapshot.reference.id, targetId: reference.content.id, onOpen: openReferenceUpdate } : undefined} /> : null}
          {projectView === 'differences' && selected && pendingImport ? <DifferencesView project={selected} importRow={pendingImport} busy={busy} onBack={() => openProjectView('cockpit')} onToggle={toggleCandidate} onAcceptAll={acceptAll} onApply={applyImport} /> : null}
          {projectView === 'differences' && selected && !pendingImport && hasReferenceUpdate && reference ? <ReferenceUpdateView project={selected} reference={reference} busy={busy} onBack={() => openProjectView('cockpit')} onDecide={(choice) => void decideProjectReference(choice)} /> : null}
          {projectView === 'differences' && selected && !pendingImport && !hasReferenceUpdate ? <DifferencesView project={selected} importRow={null} busy={busy} onBack={() => openProjectView('cockpit')} onToggle={toggleCandidate} onAcceptAll={acceptAll} onApply={applyImport} /> : null}
          {referenceView === 'overview' && reference ? <ReferenceOverview reference={reference} onCanvas={() => openReferenceView('plan-2d')} onPresentation={() => openReferenceView('presentation')} onEditor={() => openReferenceView('editor')} /> : null}
          {referenceView === 'plan-2d' && referenceProject ? <ProcessCanvas2D scope="general" project={referenceProject} onBack={() => openReferenceView('overview')} onModeChange={openReferenceView} /> : null}
          {(referenceView === 'layers-3d' || referenceView === 'pov-3d') && referenceProject ? <Suspense fallback={<div className="global-loading"><LoaderCircle className="spin" /> Préparation de la vue 3D…</div>}><ProcessCanvas3D scope="general" mode={referenceView} project={referenceProject} onBack={() => openReferenceView('overview')} onModeChange={openReferenceView} /></Suspense> : null}
          {referenceView === 'presentation' && referenceProject ? <PresentationStudio scope="general" project={referenceProject} attachments={[]} editions={presentationEditions.filter((edition) => edition.scope === 'general')} onBack={() => openReferenceView('overview')} onPublish={createPresentationEdition} onDownload={downloadPresentation} /> : null}
          {referenceView === 'editor' && reference ? <ReferenceRouteEditor reference={reference} busy={busy} onBack={() => openReferenceView('overview')} onSave={saveReference} /> : null}
          {creating ? <CreateProjectDialog busy={busy} onClose={() => setCreating(false)} onCreate={(input) => void createNewProject(input)} /> : null}
          {historyOpen && selected ? <ProjectHistoryDrawer project={selected} versions={versions} busy={busy} onClose={() => setHistoryOpen(false)} onRestore={(version) => void restoreVersion(version)} /> : null}
          {attachmentTarget && selected ? <AttachmentDrawer projectName={selected.name} stepId={attachmentTarget.stepId} stepName={attachmentTarget.stepName} attachments={attachments.filter((attachment) => attachment.step_id === attachmentTarget.stepId)} busy={busy} onClose={() => setAttachmentTarget(null)} onUpload={uploadAttachment} onUpdate={qualifyAttachment} onDownload={downloadAttachment} /> : null}
          {profileOpen ? <ProfileDrawer profile={profile} busy={busy} onClose={() => setProfileOpen(false)} onSave={saveProfile} onSetPassword={setPassword} /> : null}
          {remindersOpen ? <ReminderDrawer reminders={reminders} onClose={() => setRemindersOpen(false)} onOpenProject={openProjectById} /> : null}
          {trashOpen ? <TrashDrawer projects={trashedProjects} busy={busy} onClose={() => setTrashOpen(false)} onRestore={(project) => void restoreFromTrash(project)} /> : null}
        </>
      ) : null}
    </AppShell>
  )
}
